import { LitElement, html, css } from "lit";
import {
  customElement,
  property,
  queryAssignedElements,
  state,
} from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { apiBaseUrl } from "../../constants";
import { fetchApi, debounce } from "../../functions";
import { CarouselControls } from "../carousel/carousel-controls";
import type { Package, RelatedPackage } from "../package";

/**
 * Card deck will render a collection of cards
 * The deck defaults to a carousel view, set `static=true` to override
 */

const KEYCODE_TAB = "Tab";

@customElement("umb-market-card-deck")
export class UmbMarketCardDeck extends LitElement {
  @property({ type: String })
  header?: string;

  @property({ type: String })
  subheader?: string;

  @property({ type: Boolean })
  hideEmpty = false;

  @property({ type: Boolean })
  showDeckHeader = true;

  @property({ type: String })
  context?: "footer" | undefined;

  @property({ type: Number })
  pageSize = 12;

  @property({ type: Boolean })
  showAll? = false;

  /** optionally provide the cards for display - see related cards in package footer */
  @queryAssignedElements({ slot: "" })
  slottedCards?: Array<Package | RelatedPackage>;

  /** set to to disable carousel */
  @property({ type: Boolean })
  static = false;

  @property({ type: Boolean, attribute: "has-background" })
  hasBackground = false;

  #query: Record<string, any> | undefined;

  set query(q: Record<string, any> | undefined) {
    if (!q) return;

    this.#query = q;
    this.#getCards();
  }

  @property({ type: Object })
  get query(): Record<string, any> | undefined {
    return this.#query;
  }

  @property({ type: Boolean })
  hideLink = false;

  @state()
  private _cards: Array<Package | RelatedPackage> = [];

  @state()
  private _loading = true;

  #carouselControls!: CarouselControls;

  #currentPage: number = 1;

  #totalPages!: number;

  #totalResults!: number;

  #lastQuerystring: Record<string, string | number> = {};

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("resize", this.#debouncedResize);
    this.addEventListener("keyup", this.#handleFocus);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("resize", this.#debouncedResize);
    this.removeEventListener("keyup", this.#handleFocus);
  }

  firstUpdated() {
    if (this.slottedCards?.length) {
      this._loading = false;

      this.#carouselControls = new CarouselControls(
        this,
        this.slottedCards.length + (this.showAll ? 1 : 0)
      );
      this.#handleResize();
    }
  }

  #handleFocus = (e: KeyboardEvent) => {
    const activeElement = this.shadowRoot?.activeElement;
    if (
      e.key !== KEYCODE_TAB ||
      !activeElement ||
      !activeElement.parentNode?.children ||
      activeElement.tagName !== "UMB-MARKET-PACKAGE-CARD"
    ) {
      return;
    }

    const elementIndex = Array.from(activeElement.parentNode.children).indexOf(
      activeElement
    );

    // go next if index === card per view (ie we are at the right-most)
    // go prev if index !== 0 (ie we have moved at least one step right)
    const action =
      elementIndex >= this.#carouselControls.cardsPerView - 1 && !e.shiftKey
        ? "next"
        : "prev";

    this.#carouselControls.slide(action);
  };

  #handleResize = () => {
    this.#carouselControls?.resize();
    this.requestUpdate();
  };

  #debouncedResize = debounce(this.#handleResize);

  #generateQueryString() {
    if (!this.query) return "";

    let querystring = "?";
    if (Object.keys(this.query).length) {
      if (Object.keys(this.query).includes("maintainedBy")) {
        if (this.query.maintainedBy === "hq") {
          this.query.hQOnly = true;
        } else if (this.query.maintainedBy === "community") {
          this.query.communityOnly = true;
        }

        delete this.query.maintainedBy;
      }

      querystring += Object.entries(this.query)
        .filter((x) => x[1] !== undefined && !x[0].startsWith("_"))
        .map((x) => `${x[0]}=${x[1]}`)
        .join("&");
    }

    return querystring;
  }

  /**
   * ignore changes to fields property and any prefixed with _
   * changes to pageNumber are not treated as a change
   * @param querystring
   */
  #checkFilterChanged() {
    if (!this.query) return;

    this.query.pageSize = this.pageSize;

    const keys = Object.keys(this.query).filter(
      (x) => !x.startsWith("_") && x !== "fields" && !x.startsWith("page")
    );

    if (keys.some((k) => this.query![k] !== this.#lastQuerystring[k])) {
      this._cards = [];
      this.#currentPage = 1;
    }

    this.query.pageNumber = this.#currentPage;
    this.#lastQuerystring = keys.reduce(
      (a, v) => ({ ...a, [v]: this.query![v] }),
      {}
    );
  }

  // eslint-disable-next-line class-methods-use-this
  async #executeQuery(querystring: string): Promise<{
    totalResults: number;
    results: Array<Package>;
  }> {
    const result = await fetchApi<{
      totalResults: number;
      results: Array<Package>;
    }>(`${apiBaseUrl()}packages${querystring}`, true);

    // passing noErrorPage as true means we swallow network errors
    // to prevent routing to the error page when the request fails.
    return result ?? { totalResults: 0, results: [] };
  }

  async #getCards(showLoader: boolean = true) {
    if (!this.query) return;
    this._loading = showLoader;

    this.#checkFilterChanged();
    const querystring = this.#generateQueryString();
    const jsonResponse = await this.#executeQuery(querystring);

    // depending on context, we may want to hide the empty card deck
    // deck is hidden when empty for deck-views
    // list view always displayed as this is empty for no-result searches
    if (!jsonResponse?.results.length && this.hideEmpty) {
      this.style.display = "none";
      return;
    }

    this._cards = [
      ...(this.#currentPage > 1 ? this._cards : []),
      ...(jsonResponse?.results ?? []),
    ];

    this.#carouselControls = new CarouselControls(
      this,
      this._cards.length + (this.showAll ? 1 : 0)
    );

    this.#totalPages =
      Math.ceil((jsonResponse?.totalResults ?? 0) / this.pageSize) +
      (this.showAll ? 1 : 0);

    this.#totalResults = jsonResponse?.totalResults ?? 0;
    this._loading = false;
  }

  #getCardsToRender() {
    if (this._loading || (!this._loading && !this._cards.length)) {
      const length =
        window.innerWidth > 1023 && window.innerWidth < 1400 ? 3 : 4;

      return Array.from({ length }).map(
        (_) =>
          html`<umb-market-skeleton-card
            bones=${this.query?._queryType}
          ></umb-market-skeleton-card>`
      );
    }

    const cards = this._cards.map((card) => {
      const p = card as Package;
      return html`<umb-market-package-card
        ?deck=${true}
        .packageId=${this.context === "footer" ? p.packageId : p.id}
        .package=${p}
      ></umb-market-package-card>`;
    });

    if (this.showAll) {
      cards.push(html`<umb-market-pointer-card
        .query=${this.query}
      ></umb-market-pointer-card>`);
    }

    return cards;
  }

  render() {
    return html`
      ${when(
        this.showDeckHeader,
        () => html`<umb-market-card-deck-header
          .controls=${this.#carouselControls}
          .context=${this.context}
          .link=${this.hideLink ? undefined : `/all/${this.query?._queryType}`}
          .static=${this.static}
          .subheader=${this.subheader}
          .header=${this.header ?? "Packages"}
        ></umb-market-card-deck-header>`
      )}
      <slot
        class="card-deck ${this.static ? "static" : ""}"
        id=${this.static ? "" : "carousel"}
      >
        ${when(this.query, () => this.#getCardsToRender())}
        ${when(this._loading, () => html`<div class="alert">Loading...</div>`)}
        ${when(
          !this._loading && !this._cards.length && !this.slottedCards?.length,
          () => html`<div class="alert">No results found</div>`
        )}
      </slot>
      ${when(
        this.static && this.#currentPage < this.#totalPages,
        () =>
          html`<umb-market-button id="more"
            @click=${() => {
              this.#currentPage += 1;
              this.#getCards(false);
            }}
          >
            Show more
          </umb-market-button>`
      )}
      ${when(
        this.static && this._cards.length && !this._loading,
        () =>
          html`<small class="page-details">
            Showing ${this._cards.length} of ${this.#totalResults} results
          </small>`
      )}
      ${when(
        !this.static && this.showAll,
        () =>
          html`<umb-market-pointer-card
            standalone
            .query=${this.query}
          ></umb-market-pointer-card>`
      )}
    `;
  }

  static styles = [
    css`
      :host {
        display: block;
        width: 100%;
        position: relative;
        margin: 0 auto;
      }

      .card-deck {
        display: flex;
        transition: all 300ms ease;
        position: relative;
        flex-direction: row;
        margin-left: -7px;
        margin-right: -7px;
        transform: translateX(0);
      }

      @media (min-width: 767px) {
        .card-deck {
          --card-width: 50%;
        }
      }

      @media (min-width: 1024px) {
        .card-deck {
          --card-width: calc(100% / 3);
        }
      }

      @media (min-width: 1400px) {
        .card-deck {
          --card-width: 25%;
        }
      }

      #more {
        --button-border-radius: 100px;
        --button-text-align: center;
        --button-padding: var(--uui-size-4) var(--uui-size-6);
      }

      .static {
        flex-wrap: wrap;
        --card-margin-bottom: var(--s-spacer);
      }

      :host(:not([context="footer"])) > *:first-child {
        margin-top: var(--margin-top, var(--xxl-spacer));
      }

      :host > .static:first-child {
        margin-top: var(--xl-spacer);
      }

      .page-details {
        display: block;
        margin-top: 12px;
        width: 100%;
        text-align: right;
      }

      .alert {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        padding: 12px 18px;
        border: 1px solid var(--uui-color-default);
        color: var(--uui-color-default);
        border-radius: var(--xs-spacer);
        background: white;
        font-size: 18px;
        font-weight: 300;
      }

      :host([has-background]) {
        --background-color: var(--uui-color-current);
        --header-color: var(--uui-color-default);
        --font-weight: 400;
        background-color: var(--background-color);
        margin-top: var(--xxl-spacer);
        padding-bottom: var(--xl-spacer);
      }

      :host([has-background])::before,
      :host([has-background])::after {
        content: "";
        position: absolute;
        background-color: var(--background-color);
        top: 0;
        bottom: 0;
        z-index: -1;
      }

      :host([has-background])::before {
        left: -100%;
        width: 100%;
      }

      :host([has-background])::after {
        right: -100%;
        width: 100%;
      }

      :host([has-background]) > *:first-child {
        margin-top: var(--xl-spacer);
      }
    `,
  ];
}
